<template>
  <div>
    <!--    Loader-->
    <loading-panel :loading="loading"></loading-panel>
<!--    Actions-->
    <div class="row mt-3">
      <div class="col">
        <card>
          <div class="row mb-3">
            <div class="col">
              <tab-rail :tabs="categories" v-model="activeTabIndex"></tab-rail>
            </div>
          </div>

          <!-- Keyed so switching tabs discards the pane instead of patching
               one into the other, which let BaseAlert's fade-leave flash
               unconstrained across the page -->
          <div v-if="activeTabIndex === 0" key="gts-tab-general">

            <!-- Event tracker mode -->
            <div class="gts-section">
              <h4 class="gts-section__title">Event tracker mode</h4>
              <base-alert type="info">
                <i class="tim-icons el-icon-info"></i>
                Select <b>simple mode</b> for a quick way to collect event data in a single step.
                Enable <b>advanced mode</b> to allow event hosts to monitor and track events more closely.
              </base-alert>
              <div class="row">
                <div class="col">
                  <base-radio v-model="template.mode" name="0">Simple mode</base-radio>
                  <base-radio v-model="template.mode" name="1">Advanced mode</base-radio>
                </div>
              </div>
            </div>

            <div v-if="template.mode == 1">

              <!-- Timer -->
              <div class="gts-section">
                <h4 class="gts-section__title">Timer & event controls</h4>
                <base-alert type="info">
                  <i class="tim-icons el-icon-info"></i>
                  Configure the timer and controls available on the objectives step during events.
                </base-alert>
                <div class="row mt-2">
                  <div class="col">
                    <el-switch active-color="#e14eca"
                               v-model="template.timer_enabled"  />
                    <label> &nbsp; Enable timer</label>
                  </div>
                </div>
                <div class="row" v-if="template.timer_enabled">
                  <div class="col">
                    <el-switch active-color="#e14eca"
                               v-model="template.timer_auto_start"  />
                    <label> &nbsp; Automatically start timer on the <b>objectives</b> section</label>
                  </div>
                </div>
                <div class="row mt-2" v-if="template.timer_enabled">
                  <div class="col-lg-3">
                    <base-input label="Countdown mode">
                      <el-select
                        v-model="template.countdown_mode"
                        class="select-primary"
                        name="deposit_type"
                        placeholder="Mode"
                        value-key="id"
                      >
                        <el-option
                          v-for="(cm, index) in countdown_modes"
                          class="select-primary"
                          :label="cm.name"
                          :value="{name: cm.name, id: cm.id, label: cm.label}"
                          :key="index + 'CM'"
                        ></el-option>
                      </el-select>
                    </base-input>
                  </div>
                </div>
                <div class="row mt-2">
                  <div class="col">
                    <el-switch active-color="#e14eca"
                               v-model="template.show_complete_button"  />
                    <label> &nbsp; Allow quick completion of event from objectives step
                        <el-popover trigger="hover" placement="right">
                          <div>
                            <div class="popover-body">Adds a "Complete event now" button to the objectives step.<br/>
                              This allows event hosts to skip remaining steps by automatically<br/>
                              marking unfinished objectives and jumping to the notes step.
                            </div>
                          </div>
                          <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                        </el-popover>
                      </label>
                  </div>
                </div>
              </div>

              <!-- Analytics -->
              <div class="gts-section">
                <h4 class="gts-section__title">Analytics</h4>
                <base-alert type="info">
                  <i class="tim-icons el-icon-info"></i>
                  Give hosts live insight during the event, drawn from the history of finished events. Nothing here is shown to players.
                </base-alert>
                <div class="row mt-2">
                  <div class="col">
                    <el-switch active-color="#e14eca"
                               v-model="template.pacing_enabled"  />
                    <label> &nbsp; Enable pacing
                        <el-popover trigger="hover" placement="right">
                          <div>
                            <div class="popover-body">Shows whether the current team is ahead of or behind typical teams for this event,<br/>
                              with a projected finish on the objectives step, a comparison chip on each solved objective,<br/>
                              and a summary on the review step. Needs at least 5 finished events of the same event type.
                            </div>
                          </div>
                          <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                        </el-popover>
                      </label>
                  </div>
                </div>
              </div>

              <!-- In-room display -->
              <div class="gts-section">
                <h4 class="gts-section__title">In-room display</h4>
                <base-alert type="info">
                  <i class="tim-icons el-icon-info"></i>
                  Allow event hosts to send hints, messages, and media to a customer-facing screen during the event.
                  This combines event tracking with real-time player communication.
                </base-alert>
                <div class="row mt-2">
                  <div class="col">
                    <el-switch active-color="#e14eca"
                               v-model="template.enable_in_room_display"  />
                    <label> &nbsp; Enable in-room display</label>
                  </div>
                </div>

                <div v-if="template.enable_in_room_display" class="gts-subsection">
                  <h5 class="gts-subsection__title">Display widgets</h5>
                  <div class="row mt-1">
                    <div class="col">
                      <el-switch active-color="#e14eca"
                                 v-model="template.display_timer"  />
                      <label> &nbsp; Player screen timer</label>
                    </div>
                  </div>
                  <div v-if="template.display_timer" class="gts-nested-settings">
                    <div class="row mt-1">
                      <div class="col">
                        <el-switch active-color="#e14eca"
                                   v-model="template.display_time_adjustment"  />
                        <label> &nbsp; Time adjustment buttons
                          <el-popover trigger="hover" placement="right">
                            <div>
                              <div class="popover-body">Shows quick adjustment buttons on the timer widget<br/>
                                to add or subtract time during events.
                              </div>
                            </div>
                            <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                          </el-popover>
                        </label>
                      </div>
                    </div>
                    <div class="gts-intervals" v-if="template.display_time_adjustment">
                      <label class="gts-intervals__label">Intervals (minutes)</label>
                      <div class="gts-intervals__inputs">
                        <el-input-number :controls="false" :value="template.time_adjustment_intervals[0]"
                          @change="val => setInterval(0, val)" :min="1" :max="60" size="small" />
                        <el-input-number :controls="false" :value="template.time_adjustment_intervals[1]"
                          @change="val => setInterval(1, val)"
                          :min="(template.time_adjustment_intervals[0] || 1) + 1" :max="60" size="small" />
                        <el-input-number :controls="false" :value="template.time_adjustment_intervals[2]"
                          @change="val => setInterval(2, val)"
                          :min="(template.time_adjustment_intervals[1] || 2) + 1" :max="60" size="small" />
                      </div>
                    </div>
                    <div class="row mt-1">
                      <div class="col">
                        <el-switch active-color="#e14eca"
                                   v-model="template.display_timer_speed"  />
                        <label> &nbsp; Timer speed controls
                          <el-popover trigger="hover" placement="right">
                            <div>
                              <div class="popover-body">Shows speed controls on the timer widget to speed up<br/>
                                or slow down the timer on the customer display<br/>
                                (e.g. 0.5x, 1x, 1.5x, 2x).
                              </div>
                            </div>
                            <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                          </el-popover>
                        </label>
                      </div>
                    </div>
                    <div class="row mt-1">
                      <div class="col">
                        <el-switch active-color="#e14eca"
                                   v-model="template.enable_start_game_button"  />
                        <label> &nbsp; Start event button
                          <el-popover trigger="hover" placement="right">
                            <div>
                              <div class="popover-body">Shows a "Start event" button on the control panel that starts the timer<br/>
                                and plays event start media on the customer display. The button will only<br/>
                                appear if event start media has been uploaded in <b>communication screen<br/>
                                settings</b> for the event.
                              </div>
                            </div>
                            <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                          </el-popover>
                        </label>
                      </div>
                    </div>
                    <div class="row mt-1">
                      <div class="col">
                        <el-switch active-color="#e14eca"
                                   v-model="template.enable_complete_game_button"  />
                        <label> &nbsp; Complete event button
                          <el-popover trigger="hover" placement="right">
                            <div>
                              <div class="popover-body">Shows a "Complete event" button on the control panel that stops the timer<br/>
                                and plays event completion media on the customer display. Configure<br/>
                                event completion media in <b>communication screen settings</b> for the event.
                              </div>
                            </div>
                            <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                          </el-popover>
                        </label>
                      </div>
                    </div>
                  </div>
                  <div class="row mt-1">
                    <div class="col">
                      <el-switch active-color="#e14eca"
                                 v-model="template.display_background_music"  />
                      <label> &nbsp; Background music</label>
                    </div>
                  </div>
                  <div class="row mt-1">
                    <div class="col">
                      <el-switch active-color="#e14eca"
                                 v-model="template.display_custom_interaction"  />
                      <label> &nbsp; Custom interaction</label>
                    </div>
                  </div>
                  <div class="row mt-1">
                    <div class="col">
                      <el-switch active-color="#e14eca"
                                 v-model="template.display_hint_counter"  />
                      <label> &nbsp; Hint counter</label>
                    </div>
                  </div>
                  <div class="row mt-1">
                    <div class="col">
                      <el-switch active-color="#e14eca"
                                 v-model="template.display_mirror_view"  />
                      <label> &nbsp; Mirror display view
                        <el-popover trigger="hover" placement="right">
                          <div>
                            <div class="popover-body">When enabled, a live mirror of the customer-facing hint screen<br/>
                              will be shown alongside the objectives step during events.
                            </div>
                          </div>
                          <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                        </el-popover>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

            </div>

          </div>

          <div v-if="activeTabIndex === 1" key="gts-tab-fields">
            <div class="row">
              <div class="col">
                <b-table :items="optional_fields" :fields="['field', 'simple_mode', 'advanced_mode', 'enabled']" >

                  <template v-slot:cell(field)="row">
                    <div>{{row.item.name}}</div>
                    <small>{{row.item.description}}</small>
                  </template>
                  <template v-slot:cell(simple_mode)="row">
                    <i class="tim-icons icon-check-2 text-success mode-check" v-if="!row.item.advanced_mode_only"></i>
                    <i class="tim-icons icon-simple-remove text-danger" v-if="row.item.advanced_mode_only"></i>
                  </template>
                  <template v-slot:cell(advanced_mode)="row">
                    <i class="tim-icons icon-check-2 text-success mode-check"></i>
                  </template>
                  <template v-slot:cell(enabled)="row">
                    <el-switch active-color="#e14eca"
                               v-model="row.item.is_enabled"  />
                  </template>
                </b-table>

              </div>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col">
              <base-button type="primary" v-on:click="updateTracker()">Update settings</base-button>
            </div>
          </div>



        </card>
      </div>
    </div>

  </div>
</template>

<script>
import {Auth} from "aws-amplify";
import {API_LOCATION} from "@/api-config";
import axios from "axios";
import { BaseAlert } from 'src/components';
import TabRail from '@/components/TabRail.vue';
import _ from "lodash";
import {BTable} from "bootstrap-vue";
import {Switch, Popover, InputNumber} from "element-ui";

export default {
  name: "GameTrackerSettingsTemplate",
  components: {
    BaseAlert,
    BTable,
    TabRail,
    [Switch.name]: Switch,
    [Popover.name]: Popover,
    [InputNumber.name]: InputNumber
  },
  data() {
    return {
      loading: false,
      activeTabIndex: 0,
      categories: [
        { name: 'General settings', shortName: 'General' },
        { name: 'Optional fields', shortName: 'Fields' }
      ],
      optional_fields: [],
      countdown_modes: [
        {id: 0, name: 'Count up from 0', label: 'up'},
        {id: 1, name: 'Count down from event duration', label: 'down'}
      ],
      template: {
        mode: 0,
      }
    }
  },
  methods: {
    notifyError(message) {
      this.$notify({ type: 'danger', message, verticalAlign: 'top', horizontalAlign: 'right' })
    },
    handleRequestError(err) {
      const status = err.response ? err.response.status : null
      if (status === 401) {
        this.$router.push('/forbidden')
      } else if (status === 500) {
        this.notifyError('The feature has not been enabled as part of your subscription plan.')
        this.$router.push('/')
      } else {
        this.notifyError('Something went wrong')
      }
    },
    setInterval(index, val) {
      if (val < 1) val = 1
      this.$set(this.template.time_adjustment_intervals, index, val)

      // Ensure each subsequent value is greater than the previous
      for (let i = index + 1; i < 3; i++) {
        if (this.template.time_adjustment_intervals[i] <= this.template.time_adjustment_intervals[i - 1]) {
          this.$set(this.template.time_adjustment_intervals, i, this.template.time_adjustment_intervals[i - 1] + 1)
        }
      }
    },
    getAuthToken() {
      return new Promise(function (resolve) {
        Auth.currentSession()
          .then(data => {
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    async updateTracker(){
      this.loading = true
      const token = await this.getAuthToken()
      const config = { headers: { Authorization: token } }
      const companyId = localStorage.getItem('companyId')
      const settingsId = this.$route.query.id
      const companyGroupId = JSON.parse(localStorage.getItem('group')).id
      const url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/game-tracker-settings?id=' + settingsId

      let data = {
        mode: this.template.mode,
        countdown_mode: this.template.countdown_mode.label,
        timer_auto_start: this.template.timer_auto_start,
        timer_enabled: this.template.timer_enabled,
        enable_in_room_display: this.template.enable_in_room_display,
        display_timer: this.template.display_timer,
        display_background_music: this.template.display_background_music,
        display_custom_interaction: this.template.display_custom_interaction,
        display_hint_counter: this.template.display_hint_counter,
        display_mirror_view: this.template.display_mirror_view,
        show_complete_button: this.template.show_complete_button,
        enable_start_game_button: this.template.enable_start_game_button,
        enable_complete_game_button: this.template.enable_complete_game_button,
        display_time_adjustment: this.template.display_time_adjustment,
        time_adjustment_intervals: this.template.time_adjustment_intervals,
        display_timer_speed: this.template.display_timer_speed,
        pacing_enabled: this.template.pacing_enabled,
        fields: this.optional_fields
      }

      axios.put(url, data, config)
        .then(response => {
          this.loading = false
          this.$notify({ type: 'success', message: 'Event tracker settings have been updated', verticalAlign: 'top', horizontalAlign: 'right' })
          this.getGameTracker()
        })
        .catch(err => {
          this.loading = false
          this.handleRequestError(err)
        })
    },
    async getGameTracker() {
      this.loading = true
      const token = await this.getAuthToken()
      const config = { headers: { Authorization: token } }
      const companyId = localStorage.getItem('companyId')
      const settingsId = this.$route.query.id
      const companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/game-tracker-settings?id=' + settingsId

      axios.get(url, config)
        .then(response => {
          this.template = response.data.game_tracker_settings
          this.template.timer_enabled = Boolean(this.template.timer_enabled)
          this.template.timer_auto_start = Boolean(this.template.timer_auto_start)
          this.template.enable_in_room_display = Boolean(this.template.enable_in_room_display)
          this.template.display_timer = this.template.display_timer == null ? true : Boolean(this.template.display_timer)
          this.template.display_background_music = this.template.display_background_music == null ? true : Boolean(this.template.display_background_music)
          this.template.display_custom_interaction = this.template.display_custom_interaction == null ? true : Boolean(this.template.display_custom_interaction)
          this.template.display_hint_counter = this.template.display_hint_counter == null ? true : Boolean(this.template.display_hint_counter)
          this.template.display_mirror_view = this.template.display_mirror_view == null ? false : Boolean(this.template.display_mirror_view)
          this.template.show_complete_button = this.template.show_complete_button == null ? true : Boolean(this.template.show_complete_button)
          this.template.enable_start_game_button = this.template.enable_start_game_button == null ? false : Boolean(this.template.enable_start_game_button)
          this.template.enable_complete_game_button = this.template.enable_complete_game_button == null ? false : Boolean(this.template.enable_complete_game_button)
          this.template.display_time_adjustment = this.template.display_time_adjustment == null ? false : Boolean(this.template.display_time_adjustment)
          let intervals = this.template.time_adjustment_intervals
          if (typeof intervals === 'string') { intervals = JSON.parse(intervals) }
          this.template.time_adjustment_intervals = intervals || [1, 3, 5]
          this.template.display_timer_speed = this.template.display_timer_speed == null ? false : Boolean(this.template.display_timer_speed)
          this.template.pacing_enabled = this.template.pacing_enabled == null ? false : Boolean(this.template.pacing_enabled)
          this.template.countdown_mode = _.find(this.countdown_modes, {label: this.template.countdown_mode})
          this.optional_fields = response.data.fields
          for(let n in this.optional_fields){
            this.optional_fields[n].is_enabled = Boolean(this.optional_fields[n].is_enabled)
          }
          this.loading = false
        })
        .catch(err => {
          this.loading = false
          this.handleRequestError(err)
        })
    },
  },
  mounted(){
    this.getGameTracker()
  }
}
</script>

<style scoped>
.white-content .mode-check {
  color: #00a383 !important;
  font-weight: 700;
}
.gts-section {
  border-left: 3px solid #e14eca;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 4px;
  padding: 16px 20px;
  margin-bottom: 16px;
}

.gts-section__title {
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}


.gts-nested-settings {
  margin-left: 16px;
  margin-top: 8px;
  margin-bottom: 12px;
  padding-left: 16px;
  padding-bottom: 8px;
  border-left: 2px solid rgba(225, 78, 202, 0.3);
}

.gts-subsection {
  padding: 12px 0 0 0;
  margin-top: 4px;
}

.gts-subsection__title {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 8px;
  color: rgba(255, 255, 255, 0.7);
}
</style>

<style>
.gts-section .alert-info {
  background-color: rgba(29, 140, 248, 0.08);
  font-size: 12px;
  padding: 10px 14px;
  margin-bottom: 12px;
  display: inline-block;
}

.white-content .gts-section {
  background: rgba(0, 0, 0, 0.02);
}

.white-content .gts-section .alert-info {
  background-color: rgba(29, 140, 248, 0.06);
  color: #344675;
}

.white-content .gts-nested-settings {
  border-left-color: rgba(225, 78, 202, 0.2);
}

.white-content .gts-subsection__title {
  color: rgba(0, 0, 0, 0.55);
}

.gts-intervals {
  margin: 8px 0 4px 32px;
}

.gts-intervals__label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 6px;
  display: block;
}

.gts-intervals__inputs {
  display: flex;
  gap: 6px;
}

.gts-intervals__inputs .el-input-number {
  width: 56px !important;
  line-height: 30px;
}

.gts-intervals__inputs .el-input-number .el-input__inner {
  text-align: center;
  padding: 0 8px;
  height: 30px;
  line-height: 30px;
}

.white-content .gts-intervals__label {
  color: rgba(0, 0, 0, 0.45);
}
</style>
